import api from './api';
import {
  Reference,
  ReferenceFormData,
  ReferenceLead,
  ReferenceStats,
  ReferenceAccountsParams,
  ReferenceAccountsResponse,
  UpdateReferenceLeadAccountBody,
} from '../types/Reference';

export const referenceApi = {
  // Get all references
  getReferences: async (page: number = 1, limit: number = 20, search: string = ''): Promise<{ success: boolean; data: Reference[]; pagination: any }> => {
    const response = await api.get(`/references?page=${page}&limit=${limit}&search=${search}`);
    return response.data;
  },

  // Get reference by ID
  getReferenceById: async (id: number): Promise<{ success: boolean; data: Reference }> => {
    const response = await api.get(`/references/${id}`);
    return response.data;
  },

  // Create new reference
  createReference: async (formData: ReferenceFormData): Promise<{ success: boolean; data: Reference; message: string }> => {
    const formDataToSend = new FormData();
    
    // Personal Information
    formDataToSend.append('name', formData.name);
    formDataToSend.append('contactNumber', formData.contactNumber);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('address', formData.address);
    
    // Bank Details
    formDataToSend.append('bankAccountNumber', formData.bankAccountNumber);
    formDataToSend.append('bankAccountName', formData.bankAccountName);
    formDataToSend.append('branchName', formData.branchName);
    formDataToSend.append('bankAccountRoutingNumber', formData.bankAccountRoutingNumber);
    
    // Commission Structure
    formDataToSend.append('commissionStructure', JSON.stringify(formData.commissionStructure));
    
    // Images
    if (formData.personalImage) {
      formDataToSend.append('image', formData.personalImage);
    }
    if (formData.bankImage) {
      formDataToSend.append('bankDocument', formData.bankImage);
    }

    const response = await api.post('/references', formDataToSend, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Update reference
  updateReference: async (id: number, formData: ReferenceFormData): Promise<{ success: boolean; data: Reference; message: string }> => {
    const formDataToSend = new FormData();
    
    // Personal Information
    formDataToSend.append('name', formData.name);
    formDataToSend.append('contactNumber', formData.contactNumber);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('address', formData.address);
    
    // Bank Details
    formDataToSend.append('bankAccountNumber', formData.bankAccountNumber);
    formDataToSend.append('bankAccountName', formData.bankAccountName);
    formDataToSend.append('branchName', formData.branchName);
    formDataToSend.append('bankAccountRoutingNumber', formData.bankAccountRoutingNumber);
    
    // Commission Structure
    formDataToSend.append('commissionStructure', JSON.stringify(formData.commissionStructure));
    
    // Images
    if (formData.personalImage) {
      formDataToSend.append('image', formData.personalImage);
    }
    if (formData.bankImage) {
      formDataToSend.append('bankDocument', formData.bankImage);
    }

    const response = await api.put(`/references/${id}`, formDataToSend, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Delete reference
  deleteReference: async (id: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/references/${id}`);
    return response.data;
  },

  // Get reference leads
  getReferenceLeads: async (referenceId: number): Promise<{ success: boolean; data: ReferenceLead[] }> => {
    const response = await api.get(`/references/${referenceId}/leads`);
    return response.data;
  },

  // Get reference statistics
  getReferenceStats: async (referenceId: number): Promise<{ success: boolean; data: ReferenceStats }> => {
    const response = await api.get(`/references/${referenceId}/stats`);
    return response.data;
  },

  // Get reference accounts (leads with referral fee + payment status) — paginated
  getReferenceAccounts: async (
    referenceId: number,
    params: ReferenceAccountsParams = {}
  ): Promise<ReferenceAccountsResponse> => {
    const searchParams = new URLSearchParams();
    if (params.page != null) searchParams.set('page', String(params.page));
    if (params.limit != null) searchParams.set('limit', String(params.limit));
    if (params.status) searchParams.set('status', params.status);
    if (params.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);
    const qs = searchParams.toString();
    const url = `/references/${referenceId}/accounts${qs ? `?${qs}` : ''}`;
    const response = await api.get(url);
    return response.data;
  },

  // Update a lead's account (referral fee, payment status, payment date) for this reference
  updateReferenceLeadAccount: async (
    referenceId: number,
    leadId: number,
    body: UpdateReferenceLeadAccountBody
  ): Promise<{ success: boolean; data: { leadId: number; referralFee: number; paymentStatus: string; paymentDate: string | null }; message: string }> => {
    const response = await api.patch(`/references/${referenceId}/accounts/leads/${leadId}`, body);
    return response.data;
  },

  // Get available stages for commission structure
  getAvailableStages: async (): Promise<{ success: boolean; data: any[] }> => {
    const response = await api.get('/references/stages/available');
    return response.data;
  },

  // Get commission for reference and stage
  getCommissionForStage: async (referenceId: number, stageId: number): Promise<{ success: boolean; data: { commission: number } }> => {
    const response = await api.get(`/references/${referenceId}/commission/${stageId}`);
    return response.data;
  },

  // Get signed URL for reference image
  getImageUrl: async (referenceId: number, imageType: 'personal' | 'bank', expirationMinutes: number = 60): Promise<{ success: boolean; data: { signedUrl: string; imageUrl: string; imageType: string; expirationMinutes: number } }> => {
    const response = await api.get(`/references/image/${referenceId}/${imageType}?expiration=${expirationMinutes}`);
    return response.data;
  },
};
